import qs from 'qs';
import { ContactDTO, ContactType, FindAllContactsFilters, FindContactsTargetResponse } from '../app/lib/filters/dto';
import { JobResponse } from '../app/lib/polling/dto';
import { contactsFiltersAdapter } from '../app/lib/utils';
import { ImportCSVResponseDTO } from '../app/pages/database/dto';
import { ContactEmployeesResponse } from '../app/pages/targetList/contacts/dto';
import { ContactStateEnum, NoteDTO } from '../app/pages/targetList/dto';
import { baseApi } from './_baseQuery';
import { PersonDTO } from '../app/pages/database/people/dto';


export const contactsApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
        findAllPeopleContacts: build.query<FindContactsTargetResponse<ContactType.PERSON>, { filters: FindAllContactsFilters, targetList: string }>({
            query: ({ filters, targetList }) => ({
                url: "/contacts",
                params: qs.stringify({ ...contactsFiltersAdapter(filters, ContactType.PERSON), targetList }, {
                    arrayFormat: "repeat",
                    skipNulls: true,
                    allowEmptyArrays: false,
                    encode: true,
                })
            }),
            providesTags: [{ type: 'Contacts', id: 'people' }, { type: 'Contacts', id: 'LIST' }]
        }),
        findAllCompaniesContacts: build.query<{
            total: number
            page: number
            perPage: number
            prev: boolean
            next: boolean
            totalPage: number
            data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
        }, { filters: FindAllContactsFilters, targetList: string }>({
            async queryFn({ filters, targetList }, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    const contactsResponse = await fetchWithBQ({
                        url: "/contacts",
                        params: qs.stringify({ ...contactsFiltersAdapter(filters, ContactType.COMPANY), targetList }, {
                            arrayFormat: "repeat",
                            skipNulls: true,
                            allowEmptyArrays: false,
                            encode: true,
                        })
                    });
                    if (contactsResponse.error) return { error: contactsResponse.error };

                    const contactsData = contactsResponse.data as FindContactsTargetResponse<ContactType.COMPANY>;

                    const dataWithEmployees = await Promise.all(contactsData.data.map(async (contact) => {
                        if (!contact.contact.employeesId || contact.contact.employeesId.length === 0) {
                            return { company: contact, employees: [] };
                        }

                        const employeesPromises = contact.contact.employeesId.map(async (employeeId) => {
                            const employeeRes = await fetchWithBQ(`/person/${employeeId}`);
                            if (employeeRes.error) {
                                return null;
                            }
                            return employeeRes.data as PersonDTO;
                        });

                        const employeesResults = await Promise.all(employeesPromises);
                        const employees = employeesResults.filter((emp): emp is PersonDTO => emp !== null);
                        
                        return { company: contact, employees };
                    }));

                    return {
                        data: {
                            total: contactsData.total,
                            page: contactsData.page,
                            perPage: contactsData.perPage,
                            prev: contactsData.prev,
                            next: contactsData.next,
                            totalPage: contactsData.totalPage,
                            data: dataWithEmployees
                        }
                    };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to fetch companies contacts',
                            data: error
                        }
                    };
                }
            },
            //     url: "/contacts",
            //     params: qs.stringify({ ...contactsFiltersAdapter(filters, ContactType.COMPANY), targetList }, {
            //         arrayFormat: "repeat",
            //         skipNulls: true,
            //         allowEmptyArrays: false,
            //         encode: true,
            //     })
            // }),
            providesTags: [{ type: 'Contacts', id: 'companies' }, { type: 'Contacts', id: 'LIST' }]
        }),
        getContact: build.query<ContactDTO<ContactType>, { contactId: string }>({
            query: (contact) => ({
                url: `/contacts/${contact.contactId}`,
            }),
            providesTags: (_, __, contact) => [{ type: 'Contacts', id: contact.contactId }]
        }),
        getContactEmployees: build.query<ContactEmployeesResponse, { contactId: string }>({
            query: (contact) => ({
                url: `/contacts/${contact.contactId}/employees`,
            }),
            providesTags: (_, __, contact) => [{ type: 'Contacts', id: contact.contactId }]
        }),
        deleteBulkTargetContacts: build.mutation<{ success: boolean }, { filters: FindAllContactsFilters, targetList: string, contactType: ContactType }>({
            query: ({ filters, targetList, contactType }) => ({
                url: `contacts/bulk`,
                method: 'DELETE',
                params: qs.stringify({ ...contactsFiltersAdapter(filters, contactType), targetList, page: 0, itemsPerPage: 0 }, {
                    arrayFormat: "repeat",
                    skipNulls: true,
                    allowEmptyArrays: false,
                    encode: true,
                })
            }),
            invalidatesTags: (_result, _error, { contactType, targetList }) => [{ type: 'Contacts', id: contactType === ContactType.COMPANY ? 'companies' : 'people' }, { type: 'Targetlist', id: targetList }, { type: 'Contacts', id: 'LIST' }],
        }),
        addContactsBulk: build.mutation<{ success: boolean }, { targetListId: string, filters: FindAllContactsFilters, contactType: ContactType }>({
            query: ({ targetListId, filters, contactType }) => ({
                url: `contacts/bulk-${contactType === ContactType.COMPANY ? 'companies' : 'persons'}`,
                method: 'POST',
                params: qs.stringify({ ...contactsFiltersAdapter(filters, contactType), targetListId }, {
                    arrayFormat: "repeat",
                    skipNulls: true,
                    allowEmptyArrays: false,
                    encode: true,
                })
            }),
            invalidatesTags: (_result, _error, { contactType, targetListId }) => [{ type: 'Contacts', id: contactType === ContactType.COMPANY ? 'companies' : 'people' }, { type: 'Targetlist', id: targetListId }, { type: 'Contacts', id: 'LIST' }],
        }),
        addContacts: build.mutation<{}, { targetListId: string, contactType: ContactType, contactIds: string[] }>({
            query: ({ targetListId, contactType, contactIds }) => ({
                url: `contacts/bulk`,
                method: 'POST',
                body: {
                    targetListId,
                    contactIds: contactIds,
                    contactType
                }
            }),
            invalidatesTags: (_result, _error, { contactType, targetListId }) => [{ type: 'Contacts', id: contactType === ContactType.COMPANY ? 'companies' : 'people' }, { type: 'Targetlist', id: targetListId }, { type: 'Contacts', id: 'LIST' }],
        }),
        importContactsFromCSV: build.mutation<JobResponse, { data: FormData, contactType: ContactType, targetId: string }>({
            query: ({ data, contactType }) => ({
                url: `contacts/${contactType.toLowerCase()}-csv`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (_result, _error, { contactType, targetId }) => [{ type: 'Contacts', id: contactType === ContactType.COMPANY ? 'companies' : 'people' }, { type: 'Targetlist', id: targetId },],
        }),
        updateNotes: build.mutation<{ success: boolean }, { contactId: string, notes: NoteDTO[], contactType: ContactType }>({
            query: ({ contactId, notes }) => ({
                url: `contacts/${contactId}/notes`,
                method: "PUT",
                body: { notes },
            }),
            invalidatesTags: (_result, _error, { contactId, contactType }) => [
                { type: 'Contacts', id: contactId },
                { type: 'Contacts', id: contactType === ContactType.COMPANY ? 'companies' : 'people' },
                { type: 'Contacts', id: 'LIST' }
            ],
        }),
        updateContactState: build.mutation<{ success: boolean }, { contactId: string | null, outcome: ContactStateEnum, outcomeInfo: string | null, contactType: ContactType, targetId: string, companyId?: string }>({
            async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
                try {
                    if (!_arg.contactId) {
                        return { error: { status: 400, data: { errors: [{ message: "Contact ID is required" }] } } as { status: number, data: { errors: { message: string }[] } } };
                    }
                    const response = await fetchWithBQ({
                        url: `contacts/${_arg.contactId}`,
                        method: "PUT",
                        body: { outcome: _arg.outcome, outcomeInfo: _arg.outcomeInfo },
                    });

                    if (response.error) return { error: response.error };

                    return { data: { success: true } };
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'Failed to create person',
                            data: error
                        }
                    };
                }
            },
            invalidatesTags: (_result, _error, { contactId, contactType, targetId, companyId }) => [
                { type: 'Contacts', id: contactId! },
                { type: 'Contacts', id: companyId! },
                { type: 'Contacts', id: contactType === ContactType.COMPANY ? 'companies' : 'people' },
                { type: 'Contacts', id: 'LIST' },
                { type: 'Targetlist', id: targetId }
            ],
        }),
        restoreContacts: build.mutation<{ success: boolean }, { contacts: string[], targetList: string }>({
            query: ({ contacts }) => ({
                url: `contacts/restore`,
                method: 'PUT',
                body: contacts
            }),
            invalidatesTags: (_result, _error, { targetList }) => [{ type: 'Contacts', id: 'people' }, { type: 'Contacts', id: 'companies' }, { type: 'Contacts', id: 'LIST' }, { type: 'Targetlist', id: targetList }],
        }),
        removeContacts: build.mutation<{ success: boolean }, { contacts: string[], targetList: string }>({
            query: ({ contacts }) => ({
                url: `contacts`,
                method: 'DELETE',
                body: { ids: contacts }
            }),
            invalidatesTags: (_result, _error, { targetList }) => [{ type: 'Contacts', id: 'people' }, { type: 'Contacts', id: 'companies' }, { type: 'Contacts', id: 'LIST' }, { type: 'Targetlist', id: targetList }],
        }),
        softBulkContacts: build.mutation<{ success: boolean }, { targetListId: string, contactType: ContactType }>({
            query: ({ targetListId, contactType }) => ({
                url: `contacts/bulk-soft-delete`,
                method: 'PUT',
                params: {
                    targetList: targetListId,
                    type: contactType
                }
            }),
            invalidatesTags: (_result, _error, { contactType, targetListId }) => [{ type: 'Contacts', id: contactType === ContactType.COMPANY ? 'companies' : 'people' }, { type: 'Targetlist', id: targetListId }, { type: 'Contacts', id: 'LIST' }],
        }),
        updateFavorite: build.mutation<{ success: boolean }, { contactId: string, favorite: boolean }>({
            query: ({ contactId, favorite }) => ({
                url: `contacts/${contactId}/favorite`,
                method: 'PUT',
                body: { isFavorite: favorite }
            }),
            invalidatesTags: (_result, _error, _) => [{ type: 'Contacts', id: 'LIST' }, { type: 'Contacts', id: 'people' }, { type: 'Contacts', id: 'companies' }],
        }),
    }),
});

export const {
    useFindAllPeopleContactsQuery,
    useFindAllCompaniesContactsQuery,
    useGetContactQuery,
    useGetContactEmployeesQuery,
    useDeleteBulkTargetContactsMutation,
    useAddContactsBulkMutation,
    useAddContactsMutation,
    useImportContactsFromCSVMutation,
    useUpdateNotesMutation,
    useUpdateContactStateMutation,
    useRestoreContactsMutation,
    useRemoveContactsMutation,
    useSoftBulkContactsMutation,
    useUpdateFavoriteMutation,
} = contactsApi